import { Button, useToasts } from '@applyboard/crystal-ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfileState } from '../../clients';
import { ApiClient } from '../../clients/ApiClient';
import { useConfig } from '../../hooks/config';
import { ConfirmStateChangeDialog } from './ConfirmStateChangeDialog';

// Generic user interface that both ProfileData and AgentData should satisfy
export interface UserWithState {
  id: string;
  givenName: string;
  familyName?: string;
  state?: ProfileState; // Optional to match ProfileData structure
}

interface UserStateActionsProps {
  userData?: UserWithState;
  canManageUserState: boolean;
  redirectPath: string; // Where to redirect after state change
  userType?: string; // For success message (e.g., "User", "Agent")
}

export function UserStateActions({
  userData,
  canManageUserState,
  redirectPath,
  userType = 'User',
}: UserStateActionsProps): JSX.Element | null {
  const [userToToggle, setUserToToggle] = useState<UserWithState | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const toasts = useToasts();
  const config = useConfig();

  // Don't show state actions if no user to edit, no permissions, or no state defined
  if (!userData || !canManageUserState || !userData.state) {
    return null;
  }

  const isActive = userData.state === ProfileState.active;
  const newState = isActive ? ProfileState.inactive : ProfileState.active;
  const buttonText = isActive ? `Disable ${userType}` : `Enable ${userType}`;
  const buttonIntent = isActive ? 'negative' : 'primary';

  const handleStateToggle = () => {
    setUserToToggle(userData);
  };

  const handleConfirmToggle = async (id: string) => {
    setIsLoading(true);
    try {
      const apiClient = new ApiClient(config);
      await apiClient.editProfile({
        id,
        profileData: { state: newState },
      });

      setUserToToggle(undefined);
      navigate(redirectPath);
      toasts.positive(`Success: ${userType} ${newState === ProfileState.active ? 'enabled' : 'disabled'}.`);
    } catch (error) {
      console.error('Failed to update user state:', error);
      toasts.negative(
        new Error(
          `Error: Unable to ${newState === ProfileState.active ? 'enable' : 'disable'} ${userType.toLowerCase()}.`
        )
      );
    } finally {
      setIsLoading(false);
      setUserToToggle(undefined);
    }
  };

  return (
    <>
      <Button type="button" onClick={handleStateToggle} intent={buttonIntent} emphasis="transparent">
        {buttonText}
      </Button>

      {userToToggle && (
        <ConfirmStateChangeDialog
          user={userToToggle}
          newState={newState}
          isLoading={isLoading}
          onClose={() => setUserToToggle(undefined)}
          onConfirm={handleConfirmToggle}
          userType={userType}
        />
      )}
    </>
  );
}
